import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Clock } from 'lucide-react';
import { marketplaceApi } from '../../api/marketplace';
import CardImageLightbox from './CardImageLightbox';
import { getTeamAbbr } from '../../utils/teamAbbr';
import { useLanguage } from '../../i18n/LanguageContext';
import { useConfirmSpendD10S } from '../../context/ConfirmD10SContext';
import { refreshBalance } from '../../hooks/useBalance';

const AuctionCard = ({ auction, user, onBidSuccess, onError, onBidAuctionUpdated }) => {
  const { t } = useLanguage();
  const { confirm } = useConfirmSpendD10S();
  const [timeLeft, setTimeLeft] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [isPlacingBid, setIsPlacingBid] = useState(false);
  /** Dopo un'offerta negli ultimi N secondi, resta attivo finché il timer non torna ≥ N o timeout */
  const [pendingExtensionSync, setPendingExtensionSync] = useState(false);
  const [isCopiesOpen, setIsCopiesOpen] = useState(false);
  const [copies, setCopies] = useState([]);
  const [copiesLoading, setCopiesLoading] = useState(false);
  const [copiesError, setCopiesError] = useState('');
  const [copyBidAmounts, setCopyBidAmounts] = useState({});
  const [placingCopyBidIds, setPlacingCopyBidIds] = useState({});
  const [bidTooLowMinBid, setBidTooLowMinBid] = useState(null);

  useEffect(() => {
    const updateTime = () => {
      const end = new Date(auction.end_time);
      const now = new Date();
      const diff = end - now;
      
      if (diff <= 0) {
        setTimeLeft(t('marketplaceCards.ended'));
        setSecondsLeft(0);
        return;
      }
      
      const totalSeconds = Math.floor(diff / 1000);
      setSecondsLeft(totalSeconds);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };
    
    updateTime();
    const interval = setInterval(updateTime, 100);
    return () => clearInterval(interval);
  }, [auction.end_time, t]);

  const currentPrice = parseFloat(auction.current_price) || 0;
  const minBid = currentPrice * (1 + (parseFloat(auction.min_increment_percent) || 5) / 100);
  const availableCount = parseInt(auction.available_count, 10) || 1;
  const hasMultipleCopies = availableCount > 1;
  const isUserWinning = !!(user?.id && auction?.current_bidder?.id && user.id === auction.current_bidder.id);
  const isEnded = auction.status === 'ended' || new Date(auction.end_time) <= new Date();
  const xpLevel = auction?.card?.xp_level ?? auction?.xp_level ?? auction?.card?.xpLevel ?? auction?.xpLevel;
  const xpPercentage = auction?.card?.xp_percentage ?? auction?.xp_percentage ?? auction?.card?.xpPercentage ?? auction?.xpPercentage;
  const hasXpLevel = xpLevel !== null && xpLevel !== undefined && !Number.isNaN(Number(xpLevel));
  const hasXpPercentage = xpPercentage !== null && xpPercentage !== undefined && !Number.isNaN(Number(xpPercentage));
  const displayCardId = auction.sample_card_id ?? auction.card_id ?? auction.card?.id;
  const extensionSec = parseInt(auction.time_extension_seconds, 10) || 12;
  const bidActionsLocked = isPlacingBid || pendingExtensionSync;
  const bidBusyLabel = bidActionsLocked
    ? (isPlacingBid ? t('marketplaceCards.placingBid') : t('marketplaceCards.auctionExtending'))
    : null;

  useEffect(() => {
    setIsCopiesOpen(false);
    setCopies([]);
    setCopiesError('');
    setCopyBidAmounts({});
    setPlacingCopyBidIds({});
    setBidTooLowMinBid(null);
    setPendingExtensionSync(false);
  }, [auction.id]);

  // Sblocco sync: secondsLeft>=10 quasi mai vero dopo estensione (floor: 9.8s → 9). Usa ms + fine asta reale.
  useEffect(() => {
    if (!pendingExtensionSync) return undefined;
    const thresholdMs = Math.max(500, extensionSec * 1000 - 1200);
    const check = () => {
      const diff = new Date(auction.end_time).getTime() - Date.now();
      if (!Number.isFinite(diff) || diff <= 0) {
        setPendingExtensionSync(false);
        return;
      }
      if (diff >= thresholdMs) {
        setPendingExtensionSync(false);
      }
    };
    check();
    const id = setInterval(check, 250);
    return () => clearInterval(id);
  }, [pendingExtensionSync, auction.end_time, extensionSec]);

  useEffect(() => {
    if (!pendingExtensionSync) return undefined;
    const id = setTimeout(() => setPendingExtensionSync(false), 15000);
    return () => clearTimeout(id);
  }, [pendingExtensionSync]);

  const getTimeLeftLabel = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    if (diff <= 0) return t('marketplaceCards.ended');
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  };

  const getMinBidForAuction = (auctionItem) => {
    const price = parseFloat(auctionItem.current_price) || parseFloat(auctionItem.starting_price) || 0;
    const increment = parseFloat(auctionItem.min_increment_percent) || 5;
    return price * (1 + increment / 100);
  };

  const loadCopies = async () => {
    if (!auction.template_id) return;
    setCopiesLoading(true);
    setCopiesError('');
    try {
      const response = await marketplaceApi.getAuctionsByTemplate(auction.template_id);
      if (response?.success) {
        setCopies(response.auctions || []);
      } else {
        setCopies([]);
        setCopiesError(response?.error || t('marketplaceCards.copiesLoadFailed'));
      }
    } catch (error) {
      setCopies([]);
      setCopiesError(error.message || t('marketplaceCards.copiesLoadFailed'));
    } finally {
      setCopiesLoading(false);
    }
  };

  const handleOpenCopies = () => {
    setIsCopiesOpen(true);
    if (copies.length === 0 && !copiesLoading) {
      loadCopies();
    }
  };

  const placeBidWithAmount = async (amount) => {
    const bid = typeof amount === 'number' ? amount : parseFloat(amount);
    if (!bid || !Number.isFinite(bid)) return;
    const wasEndingSoon = secondsLeft != null && secondsLeft > 0 && secondsLeft <= extensionSec;
    setIsPlacingBid(true);
    setBidTooLowMinBid(null);
    try {
      const response = await marketplaceApi.placeBid(auction.id, {
        bidderId: user.id,
        bidAmount: bid
      });
      onBidSuccess(response);
      if (wasEndingSoon) setPendingExtensionSync(true);
      const balanceUserId = user?.isAdmin ? user?.linkedUserId : user?.id;
      refreshBalance(balanceUserId ?? user?.id);
      setBidAmount('');
    } catch (error) {
      if (error.data?.code === 'BID_TOO_LOW') {
        const serverMinBid = error.data?.minBid != null ? Number(error.data.minBid) : null;
        if (serverMinBid != null) setBidTooLowMinBid(serverMinBid);
        onError(t('marketplaceCards.bidAuctionUpdated', { amount: (serverMinBid ?? bid).toFixed(2) }));
        onBidAuctionUpdated?.();
        return;
      }
      onError(error.message || t('marketplaceCards.bidFailed'));
    } finally {
      setIsPlacingBid(false);
    }
  };

  const handlePlaceBid = async () => {
    if (!user?.id) {
      onError(t('marketplaceCards.loginToBid'));
      return;
    }

    const bid = parseFloat(bidAmount);
    if (!bid || bid < minBid) {
      onError(t('marketplaceCards.bidMinError', { amount: minBid.toFixed(2) }));
      return;
    }

    const ok = await confirm(bid, t('marketplaceCards.placeBid'));
    if (!ok) return;

    const wasEndingSoon = secondsLeft != null && secondsLeft > 0 && secondsLeft <= extensionSec;
    setIsPlacingBid(true);
    setBidTooLowMinBid(null);
    try {
      const response = await marketplaceApi.placeBid(auction.id, {
        bidderId: user.id,
        bidAmount: bid
      });
      onBidSuccess(response);
      if (wasEndingSoon) setPendingExtensionSync(true);
      const balanceUserId = user?.isAdmin ? user?.linkedUserId : user?.id;
      refreshBalance(balanceUserId ?? user?.id);
      setBidAmount('');
    } catch (error) {
      if (error.data?.code === 'BID_TOO_LOW') {
        const serverMinBid = error.data?.minBid != null ? Number(error.data.minBid) : null;
        if (serverMinBid != null) setBidTooLowMinBid(serverMinBid);
        onError(t('marketplaceCards.bidAuctionUpdated', { amount: (serverMinBid ?? minBid).toFixed(2) }));
        onBidAuctionUpdated?.();
        return;
      }
      onError(error.message || t('marketplaceCards.bidFailed'));
    } finally {
      setIsPlacingBid(false);
    }
  };

  const handleQuickBid = async () => {
    if (!user?.id) {
      onError(t('marketplaceCards.loginToBid'));
      return;
    }
    const wasEndingSoon = secondsLeft != null && secondsLeft > 0 && secondsLeft <= extensionSec;
    setIsPlacingBid(true);
    setBidTooLowMinBid(null);
    try {
      const response = await marketplaceApi.placeBid(auction.id, {
        bidderId: user.id,
        bidAmount: minBid
      });
      onBidSuccess(response);
      if (wasEndingSoon) setPendingExtensionSync(true);
      const balanceUserId = user?.isAdmin ? user?.linkedUserId : user?.id;
      refreshBalance(balanceUserId ?? user?.id);
      setBidAmount('');
    } catch (error) {
      if (error.data?.code === 'BID_TOO_LOW') {
        const serverMinBid = error.data?.minBid != null ? Number(error.data.minBid) : null;
        if (serverMinBid != null) {
          try {
            const retryResponse = await marketplaceApi.placeBid(auction.id, {
              bidderId: user.id,
              bidAmount: serverMinBid
            });
            onBidSuccess(retryResponse);
            if (wasEndingSoon) setPendingExtensionSync(true);
            const balanceUserId = user?.isAdmin ? user?.linkedUserId : user?.id;
            refreshBalance(balanceUserId ?? user?.id);
            setBidAmount('');
            return;
          } catch (retryErr) {
            if (retryErr.data?.code === 'BID_TOO_LOW') {
              const newMin = retryErr.data?.minBid != null ? Number(retryErr.data.minBid) : serverMinBid;
              setBidTooLowMinBid(newMin);
              onError(t('marketplaceCards.bidAuctionUpdated', { amount: (newMin ?? serverMinBid).toFixed(2) }));
              onBidAuctionUpdated?.();
              return;
            }
            onError(retryErr.message || t('marketplaceCards.bidFailed'));
            return;
          }
        }
        setBidTooLowMinBid(serverMinBid);
        onError(t('marketplaceCards.bidAuctionUpdated', { amount: (serverMinBid ?? minBid).toFixed(2) }));
        onBidAuctionUpdated?.();
        return;
      }
      onError(error.message || t('marketplaceCards.bidFailed'));
    } finally {
      setIsPlacingBid(false);
    }
  };

  const handleOfferMinBid = async () => {
    if (bidTooLowMinBid == null || !user?.id) return;
    const ok = await confirm(bidTooLowMinBid, t('marketplaceCards.placeBid'));
    if (!ok) return;
    await placeBidWithAmount(bidTooLowMinBid);
  };

  const handlePlaceBidForCopy = async (copyAuction, amount) => {
    if (!user?.id) {
      onError(t('marketplaceCards.loginToBid'));
      return;
    }

    const minCopyBid = getMinBidForAuction(copyAuction);
    const bid = parseFloat(amount);
    if (!bid || bid < minCopyBid) {
      onError(t('marketplaceCards.bidMinError', { amount: minCopyBid.toFixed(2) }));
      return;
    }

    const ok = await confirm(bid, t('marketplaceCards.placeBid'));
    if (!ok) return;

    setPlacingCopyBidIds((prev) => ({ ...prev, [copyAuction.id]: true }));
    try {
      const response = await marketplaceApi.placeBid(copyAuction.id, {
        bidderId: user.id,
        bidAmount: bid
      });
      onBidSuccess(response);
      const balanceUserId = user?.isAdmin ? user?.linkedUserId : user?.id;
      refreshBalance(balanceUserId ?? user?.id);
      setCopyBidAmounts((prev) => ({ ...prev, [copyAuction.id]: '' }));
      loadCopies();
    } catch (error) {
      if (error.data?.code === 'BID_TOO_LOW') {
        const amount = error.data?.minBid != null ? Number(error.data.minBid).toFixed(2) : '—';
        onError(t('marketplaceCards.bidAuctionUpdated', { amount }));
        onBidAuctionUpdated?.();
        return;
      }
      onError(error.message || t('marketplaceCards.bidFailed'));
    } finally {
      setPlacingCopyBidIds((prev) => ({ ...prev, [copyAuction.id]: false }));
    }
  };

  // Basato solo su secondsLeft così il glow continua fino a 0 (isEnded può anticipare per refresh/clock)
  const isEndingSoon = secondsLeft !== null && secondsLeft > 0 && secondsLeft <= extensionSec;

  return (
    <div className={`football-card glass-football p-6 card-hover relative ${isEndingSoon ? 'auction-ending-soon' : ''}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="marketplace-badge marketplace-badge-auction px-3 py-1 text-sm font-semibold">
          🎯 {t('marketplaceCards.auctionBadge')}
        </span>
        <div className="flex items-center gap-2">
          {hasMultipleCopies && (
            <button
              type="button"
              onClick={handleOpenCopies}
              className="bg-purple-600 text-white rounded-full px-3 py-1 text-xs font-bold shadow-lg border-2 border-white/50 hover:bg-purple-700 transition-colors"
              aria-label={t('marketplaceCards.viewCopiesAria', { count: availableCount })}
            >
              x{availableCount}
            </button>
          )}
          <div className="flex items-center text-[#64748b] text-xs font-medium">
            <Clock className="w-4 h-4 mr-1" />
            <span>{timeLeft}</span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-3 mb-3">
        <CardImageLightbox
          card={auction}
          alt={auction.player?.full_name || t('marketplaceCards.playerAlt')}
          thumbnailClassName="w-12 h-12 rounded-full object-cover border border-white/60 shadow-sm"
        />
        <h3 className="text-xl font-bold text-[#1e293b]">
          {auction.player?.full_name || `Card #${auction.card_id}`}
        </h3>
      </div>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.positionLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">{auction.player?.position || t('common.notAvailable')}</span>
        </div>
        {auction.player?.team && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#64748b] font-medium">{t('marketplaceCards.teamLabel', 'Team')}:</span>
            <span className="text-[#1e293b] font-semibold">{getTeamAbbr(auction.player.team)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.rarityLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">
            ⭐ {auction.rarity?.display_name || auction.rarity?.name || t('common.notAvailable')}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">{t('marketplaceCards.xpLevelLabel')}:</span>
          <span className="text-[#1e293b] font-semibold">
            {hasXpLevel ? `Lv ${Number(xpLevel)}${hasXpPercentage ? ` (${Number(xpPercentage).toFixed(0)}%)` : ''}` : t('common.notAvailable')}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#64748b] font-medium">Card ID:</span>
          <span className="text-[#94a3b8] font-mono text-xs">#{displayCardId ?? t('common.notAvailable')}</span>
        </div>
        <div className="border-t border-[#E2E8F0] pt-2 mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[#64748b] text-sm font-medium">{t('marketplaceCards.currentBidLabel')}:</span>
            <span className="text-[#4169E1] font-bold text-lg">{currentPrice.toFixed(2)} DIOS</span>
          </div>
          {auction.current_bidder && (
            <div className="flex items-center justify-between mb-1">
              <span className="text-[#64748b] text-xs font-medium">{t('marketplaceCards.currentWinnerLabel')}:</span>
              <span className="text-[#10B981] font-bold text-sm bg-[#10B981]/10 px-2 py-1 rounded">
                {isUserWinning ? '👑 ' : ''}{auction.current_bidder.username || t('common.unknown')}
              </span>
            </div>
          )}
          {!isEnded && (
            <div className="flex items-center justify-between">
              <span className="text-[#64748b] text-xs">{t('marketplaceCards.minBidLabel')}:</span>
              <span className="text-[#F59E0B] font-semibold text-sm">{minBid.toFixed(2)} DIOS</span>
            </div>
          )}
        </div>
      </div>
      
      {!isEnded && (!hasMultipleCopies ? (
        <div className="space-y-2">
          {bidTooLowMinBid != null && (
            <div className="rounded-lg bg-amber-500/20 border border-amber-500/50 p-3 space-y-2">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('marketplaceCards.bidAuctionUpdated', { amount: bidTooLowMinBid.toFixed(2) })}
              </p>
              <button
                type="button"
                className="btn-football w-full bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleOfferMinBid}
                disabled={bidActionsLocked || !user?.id}
              >
                {bidBusyLabel ?? `⚡ ${t('marketplaceCards.bidOfferMinButton', { amount: bidTooLowMinBid.toFixed(2) })}`}
              </button>
            </div>
          )}
          <label className="block text-xs font-semibold text-[#1e293b] mb-1">
            {t('marketplaceCards.bidAmountLabel')}
          </label>
          <input
            type="number"
            placeholder={t('marketplaceCards.bidPlaceholder', { amount: minBid.toFixed(2) })}
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            className="input-football w-full"
            min={minBid}
            step="0.01"
            disabled={bidActionsLocked}
          />
          <div className="grid grid-cols-2 gap-2">
            <button 
              className="btn-football w-full"
              onClick={handlePlaceBid}
              disabled={bidActionsLocked || !user?.id}
            >
              {bidBusyLabel ?? `🚀 ${t('marketplaceCards.placeBid')}`}
            </button>
            <button
              type="button"
              className="btn-football w-full"
              onClick={handleQuickBid}
              disabled={bidActionsLocked || !user?.id}
            >
              {bidBusyLabel ?? `⚡ ${t('marketplaceCards.quickBid')}`}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-[#64748b] font-medium">
            {t('marketplaceCards.multipleCopiesHint')}
          </div>
          <button
            type="button"
            onClick={handleOpenCopies}
            className="btn-football w-full"
          >
            {t('marketplaceCards.viewCopiesBid')}
          </button>
        </div>
      ))}

      {isCopiesOpen && ReactDOM.createPortal(
        <div
          className="marketplace-modal-overlay fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ width: '100vw', height: '100vh' }}
          onClick={() => setIsCopiesOpen(false)}
        >
          <div
            className="football-card glass-football marketplace-modal-card w-full max-w-4xl p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold uppercase tracking-wider">
                {t('marketplaceCards.availableCopies', { count: availableCount })}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsCopiesOpen(false)}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            {copiesLoading && (
              <div className="text-center py-8 text-white/70">{t('marketplaceCards.loadingCopies')}</div>
            )}

            {!copiesLoading && copiesError && (
              <div className="text-center py-6 text-red-300">{copiesError}</div>
            )}

            {!copiesLoading && !copiesError && copies.length === 0 && (
              <div className="text-center py-6 text-white/70">{t('marketplaceCards.noCopies')}</div>
            )}

            {!copiesLoading && copies.length > 0 && (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {copies.map((copy) => {
                  const copyCurrentPrice = parseFloat(copy.current_price) || parseFloat(copy.starting_price) || 0;
                  const copyMinBid = getMinBidForAuction(copy);
                  const inputValue = copyBidAmounts[copy.id] ?? '';
                  const isPlacingCopyBid = placingCopyBidIds[copy.id];
                  const copyIsEnded = copy.status === 'ended' || new Date(copy.end_time) <= new Date();
                  const copyCardId = copy.sample_card_id ?? copy.card_id ?? copy.card?.id;
                  return (
                    <div
                      key={copy.id}
                      className="border border-white/10 p-4 bg-black/40"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-white">
                          {t('marketplaceCards.auctionNumber', { id: copy.id })}
                        </div>
                        <div className="text-xs text-white/70 flex items-center">
                          <Clock className="w-4 h-4 mr-1 text-[#00D9FF]" />
                          {getTimeLeftLabel(copy.end_time)}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm mb-3">
                        <div>
                          <div className="text-white/60 text-xs">Card ID</div>
                          <div className="text-white font-mono text-xs">#{copyCardId ?? t('common.notAvailable')}</div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs">{t('marketplaceCards.currentBidLabel')}</div>
                          <div className="text-[#00D9FF] font-bold">{copyCurrentPrice.toFixed(2)} DIOS</div>
                        </div>
                        {!copyIsEnded && (
                          <div>
                            <div className="text-white/60 text-xs">{t('marketplaceCards.minBidLabel')}</div>
                            <div className="text-[#FFD700] font-bold">{copyMinBid.toFixed(2)} DIOS</div>
                          </div>
                        )}
                        <div>
                          <div className="text-white/60 text-xs">{t('marketplaceCards.currentWinnerLabel')}</div>
                          <div className="text-white font-semibold">
                            {copy.current_bidder?.username || t('marketplaceCards.none')}
                          </div>
                        </div>
                      </div>

                      {!copyIsEnded && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                          <button
                            type="button"
                            className="btn-football w-full"
                            onClick={() => handlePlaceBidForCopy(copy, copyMinBid)}
                            disabled={isPlacingCopyBid || !user?.id}
                          >
                            {isPlacingCopyBid ? t('marketplaceCards.placingBid') : `🚀 ${t('marketplaceCards.bidMin')}`}
                          </button>
                          <input
                            type="number"
                            placeholder={t('marketplaceCards.customBidPlaceholder', { amount: copyMinBid.toFixed(2) })}
                            value={inputValue}
                            onChange={(event) =>
                              setCopyBidAmounts((prev) => ({
                                ...prev,
                                [copy.id]: event.target.value
                              }))
                            }
                            className="input-football w-full"
                            min={copyMinBid}
                            step="0.01"
                          />
                          <button
                            type="button"
                            className="btn-football w-full"
                            onClick={() => handlePlaceBidForCopy(copy, inputValue)}
                            disabled={isPlacingCopyBid || !user?.id}
                          >
                            {isPlacingCopyBid ? t('marketplaceCards.placingBid') : t('marketplaceCards.placeBid')}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AuctionCard;
